import type { Props as PublisherAdProps } from "rivals/components/shared/Ads/Publisher/types";
import {
  PRIMARY_SPORTS,
  RIVALS_LIST_SPORTS,
  TEAM_RANKING_TYPES
} from "rivals/shared/constants";
import type { Site } from "rivals/shared/interfaces/Site";

export interface CustomContentProps {
  customModuleId: number;
}

export interface ProspectRankingsProps {
  sport?: PRIMARY_SPORTS;
}

export interface TeamRankingsProps {
  sport?: PRIMARY_SPORTS;
  type?: TEAM_RANKING_TYPES;
}

// Note: Passing props to components via page config should be avoided. It is only used if
// components cannot determine its expected configuration
export type PageComponentProps =
  | CustomContentProps
  | Partial<PublisherAdProps>
  | ProspectRankingsProps
  | TeamRankingsProps;

export function isProspectRankingsProps(
  props: PageComponentProps
): props is ProspectRankingsProps {
  const prProps = props as ProspectRankingsProps;

  // Return false if it is an invalid sport
  if (
    prProps.sport !== undefined &&
    !Object.values(PRIMARY_SPORTS).includes(prProps.sport)
  ) {
    return false;
  }

  return true;
}

export function isPubAdProps(
  props: PageComponentProps
): props is Partial<PublisherAdProps> {
  const pubAdProps = props as PublisherAdProps;
  return pubAdProps.href !== undefined && pubAdProps.imageUrl !== undefined;
}

export function isTeamRankingsProps(
  props: PageComponentProps
): props is TeamRankingsProps {
  const trProps = props as TeamRankingsProps;

  // Return false if it is an invalid sport
  if (
    trProps.sport !== undefined &&
    !Object.values(PRIMARY_SPORTS).includes(trProps.sport)
  ) {
    return false;
  }

  // Return false if it is an invalid type
  if (
    trProps.type !== undefined &&
    !Object.values(TEAM_RANKING_TYPES).includes(trProps.type)
  ) {
    return false;
  }

  return true;
}

export interface PageComponent {
  active: string;
  groupOrdinality: number;
  pageOrdinality: number;
  position: string;
  props: PageComponentProps;
  sport: string;
  stockComponent: ComponentType;
  subComponents: PageComponent[];
  title: ComponentType | string;
}

export interface PageConfig {
  config: {
    breakingNews: {
      headlineText: string;
      subtext?: string;
      url: string;
    } | null;
    maxTickerArticles: number;
    site: Pick<
      Site,
      "id" | "name" | "primaryColor" | "secondaryColor" | "shortName"
    >;
    sportsEnabled: RIVALS_LIST_SPORTS[];
    title: string;
    xFormallyKnownAsTwitter: {
      hashtags: string[];
      maxTweets: number | null;
      usernames: string[];
    };
  };
  layout: {
    dashboard: PageComponent[];
    leftRail: PageComponent[];
    mainBody: PageComponent[];
    rightRail: PageComponent[];
    total: number;
  };
}

export enum ComponentType {
  Commitments = "commitments",
  Community = "community",
  CustomContent = "custom_content",
  DistroScale = "distro_scale",
  Divider = "Divider",
  E2E = "e2e",
  EnrichmentModule = "enrichment_module",
  LDRB = "ldrb",
  LREC = "lrec",
  LREC2 = "lrec2",
  LREC3 = "lrec3",
  News = "news",
  PromoUpSell = "promo_upsell",
  ProspectRankingsList = "prospect_rankings",
  PubAd = "pubad",
  Scores = "scores",
  Sponsor = "sponsor",
  TeamRankings = "team_rankings",
  TeamSelector = "team_selector",
  TopTargets = "top_targets"
}
